import {
  detectMediaKind,
  extensionOf,
  makeAssetId,
  normalizeRelativePath,
  parentPathOf,
  type AssetRecord,
  type QueryRule,
  type QueryTree,
  type RawRecipe
} from "@focalfolio/domain-model";
import type { FileEntry, FileReference } from "@focalfolio/fs-contract";

function valueForRule(asset: AssetRecord, field: QueryRule["field"]): string | number | string[] {
  switch (field) {
    case "filename":
      return asset.filename;
    case "relativePath":
      return asset.relativePath;
    case "kind":
      return asset.kind;
    case "extension":
      return asset.extension;
    case "rating":
      return asset.rating;
    case "label":
      return asset.label;
    case "keywords":
      return asset.metadata.keywords;
    case "cameraModel":
      return asset.metadata.cameraModel ?? "";
    case "cameraMake":
      return asset.metadata.cameraMake ?? "";
    case "creator":
      return asset.metadata.creator ?? "";
    case "copyright":
      return asset.metadata.copyright ?? "";
    case "captureDate":
      return asset.metadata.captureDate ?? "";
    case "size":
      return asset.size;
  }
}

function compareRule(actual: string | number | string[], rule: QueryRule): boolean {
  const expected = rule.value;
  const actualArray = Array.isArray(actual) ? actual : [actual];
  const normalize = (value: string | number) => (typeof value === "string" ? value.toLocaleLowerCase("en-US") : value);
  const normalizedExpected = normalize(expected);

  return actualArray.some((candidate) => {
    const normalizedActual = normalize(candidate);
    switch (rule.comparison) {
      case "contains":
        return String(normalizedActual).includes(String(normalizedExpected));
      case "equals":
        return normalizedActual === normalizedExpected;
      case "notEquals":
        return normalizedActual !== normalizedExpected;
      case "startsWith":
        return String(normalizedActual).startsWith(String(normalizedExpected));
      case "endsWith":
        return String(normalizedActual).endsWith(String(normalizedExpected));
      case "greaterThan":
      case "after":
        return normalizedActual > normalizedExpected;
      case "greaterThanOrEqual":
        return normalizedActual >= normalizedExpected;
      case "lessThan":
      case "before":
        return normalizedActual < normalizedExpected;
      case "lessThanOrEqual":
        return normalizedActual <= normalizedExpected;
    }
  });
}

export function matchesQuery(asset: AssetRecord, query: QueryTree): boolean {
  if (query.rules.length === 0) return true;
  const results = query.rules.map((rule) => compareRule(valueForRule(asset, rule.field), rule));
  return query.operator === "and" ? results.every(Boolean) : results.some(Boolean);
}

export interface RenamePlanItem {
  assetId: string;
  source: FileReference;
  destination: FileReference;
  sourceFilename: string;
  destinationFilename: string;
  temporary?: FileReference;
  conflict?: string;
}

export interface RenameTemplateOptions {
  template: string;
  startNumber?: number;
  search?: string;
  replace?: string;
  useRegularExpression?: boolean;
  caseMode?: "keep" | "lower" | "upper" | "title";
}

function titleCase(value: string): string {
  return value.replace(/\p{L}[\p{L}\p{N}'’_-]*/gu, (word) => `${word[0]?.toLocaleUpperCase() ?? ""}${word.slice(1).toLocaleLowerCase()}`);
}

function formatDate(input: string | undefined, format = "yyyyMMdd"): string {
  const date = input ? new Date(input) : new Date();
  if (Number.isNaN(date.valueOf())) return "undated";
  const tokens: Record<string, string> = {
    yyyy: String(date.getFullYear()).padStart(4, "0"),
    MM: String(date.getMonth() + 1).padStart(2, "0"),
    dd: String(date.getDate()).padStart(2, "0"),
    HH: String(date.getHours()).padStart(2, "0"),
    mm: String(date.getMinutes()).padStart(2, "0"),
    ss: String(date.getSeconds()).padStart(2, "0")
  };
  return format.replace(/yyyy|MM|dd|HH|mm|ss/gu, (token) => tokens[token] ?? token);
}

export function renderFilenameTemplate(
  asset: AssetRecord,
  sequence: number,
  options: RenameTemplateOptions
): string {
  const extension = asset.extension;
  const originalName = extension ? asset.filename.slice(0, -(extension.length + 1)) : asset.filename;
  let name = options.template.replace(/\{([^}:]+)(?::([^}]+))?\}/gu, (_, token: string, argument: string | undefined) => {
    switch (token) {
      case "originalName":
        return originalName;
      case "extension":
        return extension;
      case "sequence":
        return String(sequence).padStart(argument?.length ?? 1, "0");
      case "captureDate":
        return formatDate(asset.metadata.captureDate, argument);
      case "modifiedDate":
        return formatDate(new Date(asset.lastModified).toISOString(), argument);
      case "cameraModel":
        return asset.metadata.cameraModel ?? "unknown-camera";
      case "cameraMake":
        return asset.metadata.cameraMake ?? "unknown-make";
      case "rating":
        return String(asset.rating);
      default:
        return "";
    }
  });
  if (options.search) {
    if (options.useRegularExpression) name = name.replace(new RegExp(options.search, "gu"), options.replace ?? "");
    else name = name.replaceAll(options.search, options.replace ?? "");
  }
  if (options.caseMode === "lower") name = name.toLocaleLowerCase();
  if (options.caseMode === "upper") name = name.toLocaleUpperCase();
  if (options.caseMode === "title") name = titleCase(name);
  name = name.replace(/[\\/:*?"<>|\u0000-\u001f]/gu, "_").replace(/[. ]+$/gu, "").trim();
  if (!name) name = `untitled-${sequence}`;
  const includesExtensionToken = options.template.includes("{extension}");
  return extension && !includesExtensionToken ? `${name}.${extension}` : name;
}

export function planBatchRename(assets: AssetRecord[], options: RenameTemplateOptions): RenamePlanItem[] {
  const start = options.startNumber ?? 1;
  const destinations = new Map<string, number>();
  const sourcePaths = new Set(assets.map((asset) => `${asset.rootId}:${asset.relativePath.toLocaleLowerCase("en-US")}`));
  const plans: RenamePlanItem[] = assets.map((asset, index) => {
    const destinationFilename = renderFilenameTemplate(asset, start + index, options);
    const destinationPath = normalizeRelativePath([asset.parentPath, destinationFilename].filter(Boolean).join("/"));
    const key = `${asset.rootId}:${destinationPath.toLocaleLowerCase("en-US")}`;
    destinations.set(key, (destinations.get(key) ?? 0) + 1);
    return {
      assetId: asset.id,
      source: { rootId: asset.rootId, relativePath: asset.relativePath },
      destination: { rootId: asset.rootId, relativePath: destinationPath },
      sourceFilename: asset.filename,
      destinationFilename
    } satisfies RenamePlanItem;
  });

  for (const plan of plans) {
    const destinationKey = `${plan.destination.rootId}:${plan.destination.relativePath.toLocaleLowerCase("en-US")}`;
    if ((destinations.get(destinationKey) ?? 0) > 1) {
      plan.conflict = "Multiple files resolve to this destination.";
      continue;
    }
    if (plan.source.relativePath === plan.destination.relativePath) continue;
    if (sourcePaths.has(destinationKey)) {
      plan.temporary = {
        rootId: plan.source.rootId,
        relativePath: normalizeRelativePath(
          [parentPathOf(plan.source.relativePath), `.focalfolio-rename-${crypto.randomUUID()}.tmp`].filter(Boolean).join("/")
        )
      };
    }
  }
  return plans;
}

export function assetFromFileEntry(rootId: string, entry: FileEntry, existing?: AssetRecord): AssetRecord {
  const now = Date.now();
  const extension = entry.name.includes(".") ? extensionOf(entry.name) : "";
  const fingerprint = `${entry.size}:${entry.lastModified}`;
  return {
    id: existing?.id ?? makeAssetId(rootId, entry.relativePath),
    rootId,
    relativePath: entry.relativePath,
    parentPath: parentPathOf(entry.relativePath),
    filename: entry.name,
    extension,
    mimeType: entry.mimeType,
    kind: detectMediaKind(entry.name, entry.mimeType),
    size: entry.size,
    lastModified: entry.lastModified,
    fingerprint,
    rating: existing?.rating ?? 0,
    label: existing?.label ?? "none",
    metadata: existing?.metadata ?? { keywords: [] },
    ...(existing?.adjustment ? { adjustment: existing.adjustment } : {}),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    missing: false
  };
}

export function recipeToCssFilter(recipe: RawRecipe): string {
  const exposure = Math.max(0.05, 1 + recipe.exposure * 0.35);
  const contrast = Math.max(0, 1 + recipe.contrast / 100);
  const saturation = Math.max(0, 1 + recipe.saturation / 100);
  const warmth = recipe.temperature / 100;
  const sepia = Math.max(0, warmth * 0.12);
  const hue = recipe.tint * 0.08;
  return `brightness(${exposure}) contrast(${contrast}) saturate(${saturation}) sepia(${sepia}) hue-rotate(${hue}deg)`;
}

export interface RuntimeCapabilities {
  secureContext: boolean;
  fileSystemAccess: boolean;
  indexedDb: boolean;
  opfs: boolean;
  webWorkers: boolean;
  offscreenCanvas: boolean;
  webCodecs: boolean;
  webGpu: boolean;
  crossOriginIsolated: boolean;
  serviceWorker: boolean;
  persistentStorage: boolean;
}

export function detectRuntimeCapabilities(): RuntimeCapabilities {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const win = typeof window === "undefined" ? undefined : window;
  return {
    secureContext: Boolean(win?.isSecureContext),
    fileSystemAccess: Boolean(win && "showDirectoryPicker" in win),
    indexedDb: typeof indexedDB !== "undefined",
    opfs: Boolean(nav?.storage && "getDirectory" in nav.storage),
    webWorkers: typeof Worker !== "undefined",
    offscreenCanvas: typeof OffscreenCanvas !== "undefined",
    webCodecs: typeof VideoDecoder !== "undefined",
    webGpu: Boolean(nav && "gpu" in nav),
    crossOriginIsolated: Boolean(globalThis.crossOriginIsolated),
    serviceWorker: Boolean(nav && "serviceWorker" in nav),
    persistentStorage: Boolean(nav?.storage?.persist)
  };
}

export function createDiagnosticReport(input: {
  applicationVersion: string;
  roots: Array<{ id: string; name: string; permission: string }>;
  assetCount: number;
  jobs: Array<{ id: string; type: string; status: string; error?: string }>;
  messages: Array<{ level: string; message: string; at: number }>;
}): Record<string, unknown> {
  return {
    format: "focalfolio-diagnostics",
    version: 1,
    generatedAt: new Date().toISOString(),
    applicationVersion: input.applicationVersion,
    userAgent: typeof navigator === "undefined" ? "unknown" : navigator.userAgent,
    capabilities: detectRuntimeCapabilities(),
    roots: input.roots.map((root, index) => ({
      id: `root-${index + 1}`,
      nameHash: hashText(root.name),
      permission: root.permission
    })),
    assetCount: input.assetCount,
    jobs: input.jobs.map((job) => ({ ...job, error: job.error ? redactPaths(job.error) : undefined })),
    messages: input.messages.map((entry) => ({ ...entry, message: redactPaths(entry.message) }))
  };
}

function hashText(input: string): string {
  let value = 5381;
  for (const character of input) value = (value * 33) ^ character.charCodeAt(0);
  return (value >>> 0).toString(16).padStart(8, "0");
}

export function redactPaths(input: string): string {
  return input
    .replace(/[A-Za-z]:\\(?:[^\\\s]+\\)*[^\\\s]*/gu, "[REDACTED_PATH]")
    .replace(/\/(?:Users|home|mnt|media|Volumes)\/[^\s"']+/gu, "[REDACTED_PATH]");
}

export async function acquireWorkspaceWriterLock(
  onAcquired: () => Promise<void> | void,
  onUnavailable: () => Promise<void> | void
): Promise<void> {
  if (!navigator.locks) {
    await onAcquired();
    return;
  }
  await navigator.locks.request("focalfolio-catalog-writer", { ifAvailable: true, mode: "exclusive" }, async (lock) => {
    if (!lock) {
      await onUnavailable();
      return;
    }
    await onAcquired();
    await new Promise<void>(() => {
      // Deliberately keep the lock for the life of the tab.
    });
  });
}
